import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { Component } from "react";
import { Card, CardHeader, CardBody, Button } from "reactstrap";
import classNames from "classnames";
import { Toast } from "primereact/toast";
import { issuingApiService } from "../../services/api/Issuing/IssuingApiService";
import { CardInfo, DocumentRequest, DocumentRequestFile, DocumentRequestStatus } from "../../models/IssuingClientsModel";
import history from "../../history";
import { Fieldset } from "primereact/fieldset";
import { fileService } from "../../services/FileService";
import { confirmDialog } from "primereact/confirmdialog";

interface ClientCardsProps {
    IndividualId: number | undefined;
}

interface ClientCardsDefaultState {
    isLoading: boolean;
    documentRequests: DocumentRequest[] | null;
    expandedRows: any | null;
    pendingUploads: {
        [documentRequestId: number]: File[];
    };
}

export default class DocumentRequestComponent extends Component<ClientCardsProps, ClientCardsDefaultState> {
    toast: Toast | null = null;

    constructor(props: ClientCardsProps) {
        super(props);

        this.state = {
            isLoading: true,
            documentRequests: null,
            expandedRows: null,
            pendingUploads: {}
        };
    }

    async componentDidMount() {
        this.loadDocuments()
    }
    loadDocuments = async () => {
        const paymentMethodsResult = await issuingApiService.getClientDocumentRequests(this.props.IndividualId!);

        if (!paymentMethodsResult.success) {
            this.setState({ isLoading: false });
            this.toast?.show({ severity: "error", summary: "Error", detail: paymentMethodsResult.message, life: 5000 });
            return;
        }
        this.setState({
            isLoading: false,
            documentRequests: [...paymentMethodsResult.data],
        });
    }
    onFilesSelected = (
        event: React.ChangeEvent<HTMLInputElement>,
        requestId: number
    ) => {
        const fileList = event.target.files;
        if (!fileList || fileList.length === 0) return;

        const newFiles = Array.from(fileList);

        this.setState((prev: any) => {
            const request = prev.documentRequests?.find(
                (r: any) => r.documentRequestId === requestId
            );

            const uploadedCount = request?.files?.length || 0;
            const pendingFiles = prev.pendingUploads[requestId] || [];

            const totalExisting = uploadedCount + pendingFiles.length;
            const remainingSlots = 3 - totalExisting;

            if (remainingSlots <= 0) {
                this.toast?.show({
                    severity: "warn",
                    summary: "Limit reached",
                    detail: "Maximum 3 files allowed per request",
                    life: 5000,
                });
                return prev;
            }

            const allowedNewFiles = newFiles.slice(0, remainingSlots);

            if (newFiles.length > remainingSlots) {
                this.toast?.show({
                    severity: "warn",
                    summary: "Some files ignored",
                    detail: `Only ${remainingSlots} file(s) allowed for this request`,
                    life: 5000,
                });
            }

            return {
                pendingUploads: {
                    ...prev.pendingUploads,
                    [requestId]: [...pendingFiles, ...allowedNewFiles],
                },
            };
        });

        event.target.value = "";
    };


    sendFiles = async (requestId: number) => {
        const { IndividualId } = this.props;

        if (!IndividualId) {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: "IndividualId is missing",
                life: 5000,
            });
            return;
        }

        this.setState({ isLoading: true });

        try {
            const payload = {
                individualId: IndividualId,
                documentRequestId: requestId,
            };

            const result = await issuingApiService.submitDocuments(payload);

            if (!result.success) {
                this.toast?.show({
                    severity: "error",
                    summary: "Error",
                    detail: result.message,
                    life: 5000,
                });
                return;
            }

            this.toast?.show({
                severity: "success",
                summary: "Sent for review",
                life: 5000,
            });

            await this.loadDocuments();

        } catch (err: any) {
            this.toast?.show({
                severity: "error",
                summary: "Sending failed",
                detail: err.message || "Request failed",
                life: 5000,
            });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    saveFiles = async (requestId: number) => {
        const { pendingUploads } = this.state;
        const { IndividualId } = this.props;

        const files = pendingUploads[requestId];

        if (!files || files.length === 0) return;

        if (!IndividualId) {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: "IndividualId is missing",
                life: 5000,
            });
            return;
        }

        this.setState({ isLoading: true });

        try {
            const filesPayload = await Promise.all(
                files.map(async (file) => ({
                    fileName: file.name,
                    fileContent: await this.toBase64(file),
                }))
            );

            const payload = {
                individualId: IndividualId,
                documentRequestId: requestId,
                files: filesPayload,
            };

            const result = await issuingApiService.uploadDocument(payload);

            if (!result.success) {
                this.toast?.show({
                    severity: "error",
                    summary: "Error",
                    detail: result.message,
                    life: 5000,
                });
                return;
            }

            this.setState(prev => {
                const updated = { ...prev.pendingUploads };
                delete updated[requestId];

                return {
                    pendingUploads: updated,
                };
            });

            this.toast?.show({
                severity: "success",
                summary: "Uploaded",
                detail: `${files.length} ${files.length === 1 ? "file" : "files"} uploaded successfully`,
                life: 5000,
            });

            await this.loadDocuments();

        } catch (err: any) {
            this.toast?.show({
                severity: "error",
                summary: "Upload failed",
                detail: err.message || "Upload error",
                life: 5000,
            });
        } finally {
            this.setState({ isLoading: false });
        }
    };


    toBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                // remove "data:xxx;base64,"
                const base64 = (reader.result as string).split(",")[1];
                resolve(base64);
            };
            reader.onerror = error => reject(error);
        });
    }

    onDownloadClick = async (fileStorageId: string, fileName: string) => {
        this.setState({ isLoading: true });

        try {
            const imagePath = await fileService.GetFile(fileStorageId);

            if (!imagePath.success) {
                this.toast?.show({
                    severity: "error",
                    summary: "Error",
                    detail: imagePath.message,
                    life: 5000,
                });
                return;
            }

            const base64 = imagePath.data;

            // detect svg
            const isSvg = base64.startsWith("PHN2Zy") || fileName.endsWith(".svg");

            const mimeType = isSvg ? "image/svg+xml" : "image/png";

            const url = `data:${mimeType};base64,${base64}`;

            const link = document.createElement("a");
            link.href = url;

            // IMPORTANT: give proper extension
            link.download = fileName || (isSvg ? "image.svg" : "file.png");

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

        } catch (err: any) {
            this.toast?.show({
                severity: "error",
                summary: "Download failed",
                detail: err.message || "Unexpected error",
                life: 5000,
            });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    onDeleteDocumentClick = async (documentRequestId: number, fileId: number) => {
        const { IndividualId } = this.props;

        if (!IndividualId) {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: "IndividualId is missing",
                life: 5000,
            });
            return;
        }

        confirmDialog({
            message: "Are you sure you want to delete this document?",
            header: "Delete Confirmation",
            icon: "pi pi-exclamation-triangle",

            accept: async () => {
                this.setState({ isLoading: true });

                try {
                    const response = await issuingApiService.deleteRequestDocument({
                        individualId: IndividualId,
                        documentRequestId,
                        fileId,
                    });

                    if (!response.success) {
                        this.toast?.show({
                            severity: "error",
                            summary: "Delete failed",
                            detail: response.message || "Delete error",
                            life: 5000,
                        });
                        return;
                    }

                    this.toast?.show({
                        severity: "success",
                        summary: "Deleted",
                        detail: "Document deleted successfully",
                        life: 5000,
                    });

                    await this.loadDocuments();

                } catch (err: any) {
                    this.toast?.show({
                        severity: "error",
                        summary: "Delete failed",
                        detail: err.message || "Unexpected error",
                        life: 5000,
                    });
                } finally {
                    this.setState({ isLoading: false });
                }
            },

            reject: () => {
                // optional: do nothing or toast
            },
        });
    };

    DocumentActions = (request: DocumentRequest, row: any) => {
        if (!row.storageId) return null;

        return (
            <>
                <Button
                    size="sm"
                    color="primary"
                    outline
                    onClick={() => this.onDownloadClick(row.storageId, "")}
                >
                    Download
                </Button>

                {request.documentRequestStatus === DocumentRequestStatus.Requested && (
                    <Button
                        size="sm"
                        color="danger"
                        outline
                        className="ml-2"
                        onClick={() =>
                            this.onDeleteDocumentClick(request.documentRequestId, row.id)
                        }
                    >
                        Delete
                    </Button>
                )}
            </>
        );
    };
    filesTemplate = (request: DocumentRequest) => {
        const uploadedFiles = request.files || [];
        const pendingFiles = this.state.pendingUploads[request.documentRequestId] || [];

        return (
            <div className="doc-expanded-wrapper">
                {(uploadedFiles.length > 0 || pendingFiles.length > 0) && (
                    <DataTable
                        value={[
                            ...uploadedFiles.map(f => ({
                                id: f.id,
                                name: f.fileName,
                                status: "Uploaded",
                                storageId: f.fileStorageId,
                            })),
                            ...pendingFiles.map((f, idx) => ({
                                id: "",
                                name: f.name,
                                status: "Pending",
                                storageId: "-",
                                index: idx,
                            })),
                        ]}
                        responsiveLayout="scroll"
                    >
                        <Column field="name" header="File Name" />
                        <Column field="status" header="Status" />
                        <Column field="storageId" header="Storage Id" />

                        <Column
                            header="Action"
                            body={(row) =>
                                row.status === DocumentRequestStatus.Pending ? (
                                    <>
                                        <Button
                                            size="sm"
                                            color="danger"
                                            onClick={() =>
                                                this.removePendingFile(request.documentRequestId, row.index)
                                            }
                                        >
                                            Delete
                                        </Button>
                                        {row.storageId !== "-" && this.DocumentActions(request, row)}
                                    </>
                                ) : (
                                    row.storageId !== "-" && this.DocumentActions(request, row)
                                )
                            }
                        />

                    </DataTable>
                )}

                {request.documentRequestStatus === DocumentRequestStatus.Requested &&
                    <div className="text-center mb-4">
                        {/* Upload Section */}
                        <div
                            className="file-upload-area mt-2 upload-doc-area "

                            onClick={() => document.getElementById(`upload-${request.documentRequestId}`)?.click()}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                (e.currentTarget as HTMLDivElement).style.backgroundColor = "#e6f0ff";
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                (e.currentTarget as HTMLDivElement).style.backgroundColor = "transparent";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                (e.currentTarget as HTMLDivElement).style.backgroundColor = "transparent";
                                const files = e.dataTransfer.files;
                                if (files.length > 0) {
                                    this.onFilesSelected({ target: { files } } as any, request.documentRequestId);
                                }
                            }}
                        >
                            <i
                                className="fas fa-cloud-upload-alt upload-icon"
                                style={{ fontSize: "2.5rem", color: "#007bff", marginBottom: "0.5rem" }}
                            ></i>
                            <div style={{ fontSize: "0.95rem", color: "#555" }}>
                                Click or drag & drop files here
                            </div>
                            <input
                                type="file"
                                multiple
                                id={`upload-${request.documentRequestId}`}
                                style={{ display: "none" }}
                                onChange={(e) => this.onFilesSelected(e, request.documentRequestId)}
                            />
                        </div>

                        {/* Submit Button */}
                        {pendingFiles.length > 0 && (
                            <Button
                                color="success"
                                size="sm"
                                className="mt-3"
                                onClick={() => this.saveFiles(request.documentRequestId)}
                            >
                                Save {pendingFiles.length > 0
                                    ? `(${pendingFiles.length}) ${pendingFiles.length === 1 ? "File" : "Files"}`
                                    : ""}     </Button>
                        )}
                        {pendingFiles.length === 0 && uploadedFiles.length > 0 && (
                            <Button
                                color="success"
                                size="sm"
                                className="mt-3"
                                onClick={() => this.sendFiles(request.documentRequestId)}
                            >
                                Send To ICard
                            </Button>
                        )}
                    </div>
                }
                {/* } */}
            </div>
        );
    };

    onCardDetailsActionButtonClick = (card: CardInfo) => {
        history.push({
            pathname: `/issuing/card/${card.cardId}/${this.props.IndividualId}`
        });
    };
    removePendingFile = (requestId: number, index: number) => {
        this.setState(prev => {
            const updatedFiles = prev.pendingUploads[requestId]?.slice() || [];
            updatedFiles.splice(index, 1); // remove the file at index

            return {
                pendingUploads: {
                    ...prev.pendingUploads,
                    [requestId]: updatedFiles,
                },
            };
        });
    };

    renderStatusBadge(success: boolean, title: string) {
        return <div className={classNames("badge", "badge-" + (success ? "success" : "secondary"))}>{title}</div>;
    }
    renderClientRequestedDocuments = () => {
        return (
            this.state.documentRequests && (
                <Card>
                    <Toast ref={(el) => (this.toast = el)} />
                    <CardBody>
                        <Fieldset legend="Requested Documents" toggleable={true} collapsed={false}>
                            <DataTable
                                value={this.state.documentRequests}
                                loading={this.state.isLoading}
                                expandedRows={this.state.expandedRows}
                                // onRowToggle={(e) => this.setState({ expandedRows: e.data })}
                                rowExpansionTemplate={this.filesTemplate}
                                dataKey="documentRequestId"
                                onRowExpand={(e) => {
                                    this.setState({
                                        expandedRows: {
                                            [e.data.documentRequestId]: true,
                                        },
                                    });
                                }}
                                onRowCollapse={() => {
                                    this.setState({ expandedRows: null });
                                }}
                                rowClassName={(rowData) => {
                                    const isExpanded =
                                        this.state.expandedRows &&
                                        this.state.expandedRows[rowData.documentRequestId];

                                    return isExpanded ? "expanded-row" : "";
                                }}
                            >
                                <Column expander style={{ width: "3em" }} />
                                <Column field="documentRequestId" header="Request ID" />
                                <Column field="documentRequesTypetId" header="Type" />

                                <Column
                                    header="Status"
                                    body={(rowData: DocumentRequest) =>
                                        this.renderStatusBadge(
                                            rowData.documentRequestStatus == DocumentRequestStatus.Provided,
                                            rowData.documentRequestStatus,
                                        )
                                    }
                                />
                                <Column field="icardNote" header="Note" />



                            </DataTable>


                        </Fieldset>

                    </CardBody>
                </Card>
            )
        );
    };

    render() {
        return this.renderClientRequestedDocuments();
    }
}
